import {
  exportReportToExcel,
  exportReportToPDF,
  type ReportExportOptions,
  type ReportExportTable,
} from '../../utils/reportExport';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import type {
  MgaBondedLine,
  MgaCashEntry,
  MgaCrewLine,
  MgaCrewRequest,
  MgaPeriod,
} from './useData';
import type { MgaExportPayload } from './exportPeriod';

function n(v: number | null | undefined) {
  return Number.isFinite(Number(v)) ? Number(v) : 0;
}

function round2(v: number) {
  return Math.round(v * 100) / 100;
}

function money(v: number | null | undefined) {
  return round2(n(v));
}

function periodTitle(
  period: Pick<MgaPeriod, 'vesselId' | 'vessel' | 'periodFrom' | 'periodTo'>,
) {
  const vessel = period.vessel?.vesselName ?? `Vessel #${period.vesselId}`;
  return `${vessel} — ${formatDateGlobal(period.periodFrom)} → ${formatDateGlobal(period.periodTo)}`;
}

function fileSlug(
  period: Pick<MgaPeriod, 'vessel' | 'periodFrom'>,
  suffix: string,
) {
  const abrv = (period.vessel?.vesselAbrv || period.vessel?.vesselName || 'vessel')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 40);
  const from = String(period.periodFrom).slice(0, 10);
  return `MGA_${abrv}_${from}_${suffix}`;
}

function cashBal(cash: MgaCashEntry[]) {
  return cash.reduce((s, e) => {
    const amt = n(e.amountPeriod ?? e.amount);
    return e.entryType === 'EXPENSE' ? s - amt : s + amt;
  }, 0);
}

function bondedBal(bonded: MgaBondedLine[]) {
  const inn = bonded
    .filter((l) => l.section === 'STOCK' || l.section === 'INCOMING')
    .reduce((s, r) => s + n(r.amount), 0);
  const out = bonded.filter((l) => l.section === 'EXPENSE').reduce((s, r) => s + n(r.amount), 0);
  return inn - out;
}

/** Master’s close pack — signed summary for office / owners. */
export async function exportMgaClosePackPdf(payload: MgaExportPayload) {
  const { period, cash, bonded, requests } = payload;
  const ccy = period.currency || 'USD';
  const lines = period.lines ?? [];
  const title = `MGA Close Pack — ${periodTitle(period)}`;

  const cover: ReportExportTable = {
    headers: ['Field', 'Value'],
    rows: [
      ['Vessel', period.vessel?.vesselName || `Vessel #${period.vesselId}`],
      ['Code', period.vessel?.vesselAbrv || ''],
      ['Period from', formatDateGlobal(period.periodFrom)],
      ['Period to', formatDateGlobal(period.periodTo)],
      ['Currency', ccy],
      ['Status', period.status],
      ['Crew lines', lines.length],
      ['Master approved by', period.masterApprovedByName || '—'],
      [
        'Master approved at',
        period.masterApprovedAt ? formatDateGlobal(period.masterApprovedAt) : '—',
      ],
      ['Closed at', period.closedAt ? formatDateGlobal(period.closedAt) : '—'],
      [
        'Finance journal',
        period.accDocument?.code ||
          (period.accDocumentId ? `#${period.accDocumentId}` : '—'),
      ],
    ],
  };

  const totals: ReportExportTable = {
    headers: ['Metric', 'Amount', 'CCY'],
    rows: [
      ['Gross payable', money(period.totals?.grossPayable), ccy],
      ['OT total', money(period.totals?.otTotal), ccy],
      ['Extra earnings', money(period.totals?.extraEarnings), ccy],
      ['Previous balance', money(period.totals?.previousBalance), ccy],
      ['Total deductions', money(period.totals?.totalDeductions ?? period.totals?.deductions), ccy],
      ['Allotments', money(period.totals?.allotmentAmount), ccy],
      ['Net due to crew', money(period.totals?.netDue), ccy],
      ['Cash on hand', money(cashBal(cash)), ccy],
      ['Bonded balance', money(bondedBal(bonded)), ccy],
      [
        'Cash by master (requests)',
        money(
          requests
            .filter((r) => r.requestType === 'CASH_BY_MASTER')
            .reduce((s, r) => s + n(r.amount), 0),
        ),
        ccy,
      ],
      [
        'Allotment requests',
        money(
          requests.filter((r) => r.requestType === 'ALLOTMENT').reduce((s, r) => s + n(r.amount), 0),
        ),
        ccy,
      ],
    ],
  };

  const payroll: ReportExportTable = {
    headers: ['Crew', 'Rank', 'Days', 'Gross', 'Deductions', 'Net due'],
    rows: lines.map((l) => [
      l.crewName,
      l.rankName || '',
      money(l.daysInPeriod),
      money(l.grossPayable),
      money(l.totalDeductions ?? l.deductions),
      money(l.netDue),
    ]),
  };

  const signatures: ReportExportTable = {
    headers: ['Role', 'Name', 'Date', 'Signature'],
    rows: [
      [
        'Master',
        period.masterApprovedByName || '',
        period.masterApprovedAt ? formatDateGlobal(period.masterApprovedAt) : '',
        '',
      ],
      ['Office / Superintendent', '', '', ''],
      ['Accounts', '', '', ''],
    ],
  };

  const opts: ReportExportOptions = {
    reportTitle: title,
    fileName: fileSlug(period, 'ClosePack'),
    table: cover,
    additionalPdfSections: [
      { sectionTitle: 'Period totals', table: totals, rightAlignColumnIndices: [1] },
      {
        sectionTitle: 'Payroll summary',
        table: payroll,
        rightAlignColumnIndices: [2, 3, 4, 5],
      },
      { sectionTitle: 'Approvals / signatures', table: signatures },
    ],
  };
  await exportReportToPDF(opts);
}

/** One PDF with a compact payslip section per crew. */
export async function exportMgaBulkPayslipsPdf(payload: MgaExportPayload) {
  const { period } = payload;
  const lines = [...(period.lines ?? [])].sort((a, b) =>
    a.crewName.localeCompare(b.crewName, undefined, { sensitivity: 'base' }),
  );
  const ccy = period.currency || 'USD';
  const title = `Payslips — ${periodTitle(period)}`;

  if (lines.length === 0) {
    throw new Error('No crew lines to export.');
  }

  const first = payslipSection(lines[0], ccy);
  const rest = lines.slice(1).map((l) => ({
    sectionTitle: `${l.crewName}${l.rankName ? ` — ${l.rankName}` : ''}`,
    table: payslipSection(l, ccy),
    rightAlignColumnIndices: [1],
  }));

  await exportReportToPDF({
    reportTitle: title,
    fileName: fileSlug(period, 'Payslips'),
    table: first,
    rightAlignColumnIndices: [1],
    additionalPdfSections: rest,
  });
}

/** Single crew monthly pay account PDF. */
export async function exportMgaPayslipPdf(
  period: Pick<
    MgaPeriod,
    'id' | 'vesselId' | 'vessel' | 'periodFrom' | 'periodTo' | 'status' | 'currency'
  >,
  line: MgaCrewLine,
) {
  const ccy = period.currency || 'USD';
  const crewSlug = (line.crewName || `line_${line.id}`)
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 40);
  await exportReportToPDF({
    reportTitle: `Monthly pay account — ${line.crewName}${line.rankName ? ` (${line.rankName})` : ''} — ${periodTitle(period)}`,
    fileName: fileSlug(period, `Payslip_${crewSlug}`),
    table: payslipSection(line, ccy),
    rightAlignColumnIndices: [1],
  });
}

function payslipSection(line: MgaCrewLine, ccy: string): ReportExportTable {
  const ot = money(line.otTotal ?? n(line.overtimeHours) * n(line.overtimeRate));
  const rows: (string | number)[][] = [
    ['Crew', line.crewName],
    ['Rank', line.rankName || ''],
    ['Currency', ccy],
    ['Days in period', money(line.daysInPeriod)],
    ['Previous balance', money(line.previousBalance)],
    ['Extra earnings', money(line.extraEarnings)],
    ['Overtime', ot],
  ];
  for (const v of line.wageValues ?? []) {
    if (v.columnType === 'INFO' || !v.includeInTotal) continue;
    if (Math.abs(n(v.periodAmount)) < 0.0001) continue;
    const prefix = v.columnType === 'DEDUCTION' ? '− ' : '';
    rows.push([`${prefix}${v.columnName || v.columnCode}`, money(v.periodAmount)]);
  }
  rows.push(
    ['Cash advance (office)', money(line.cashAdvanceOffice)],
    ['Cash advance (master)', money(line.cashAdvanceMaster)],
    ['Allotments', money(line.allotmentAmount)],
    ['Cigarettes', money(line.cigarettesAmount)],
    ['Internet cards', money(line.internetCardsAmount)],
    ['Other bonded', money(line.otherBondedAmount)],
    ['Various', money(line.variousDeductions)],
    ['Gross payable', money(line.grossPayable)],
    ['Total deductions', money(line.totalDeductions ?? line.deductions)],
    ['Net due', money(line.netDue)],
  );
  return { headers: ['Item', 'Amount'], rows };
}

export type MgaRemittanceRow = {
  crewName: string;
  rankName?: string | null;
  amount: number;
  currency?: string | null;
  entryDate?: string | null;
  remarks?: string | null;
  allotteeName?: string | null;
  bank?: string | null;
  bankAccount?: string | null;
  mgaCrewRequestId?: number;
};

/** Allotment / remittance list for shore payroll. */
export async function exportMgaAllotmentRemittance(
  period: MgaPeriod,
  rows: MgaRemittanceRow[],
  format: 'excel' | 'pdf' | 'csv',
) {
  const ccy = period.currency || 'USD';
  const headers = [
    'Crew',
    'Rank',
    'Allottee',
    'Bank',
    'Account',
    'Amount',
    'CCY',
    'Date',
    'Remarks / ref',
  ];
  const tableRows = rows.map((r) => [
    r.crewName,
    r.rankName || '',
    r.allotteeName || '',
    r.bank || '',
    r.bankAccount || '',
    money(r.amount),
    r.currency || ccy,
    r.entryDate ? formatDateGlobal(r.entryDate) : '',
    r.remarks || '',
  ]);

  if (format === 'csv') {
    await exportMgaAllotmentBankCsv(period, rows);
    return;
  }

  const table: ReportExportTable = {
    headers,
    rows: tableRows,
  };
  const opts: ReportExportOptions = {
    reportTitle: `Allotment remittance — ${periodTitle(period)}`,
    fileName: fileSlug(period, 'Allotments'),
    table,
    rightAlignColumnIndices: [5],
  };
  if (format === 'excel') await exportReportToExcel(opts);
  else await exportReportToPDF(opts);
}

/**
 * Fixed-layout CSV for bank / payroll upload (UTF-8 BOM).
 * Column order is stable so banks can map fields without Excel formatting.
 */
export async function exportMgaAllotmentBankCsv(period: MgaPeriod, rows: MgaRemittanceRow[]) {
  const ccy = period.currency || 'USD';
  const headers = [
    'BeneficiaryName',
    'BankName',
    'AccountNumber',
    'Amount',
    'Currency',
    'ValueDate',
    'PaymentReference',
    'CrewName',
    'Rank',
    'Remarks',
  ];
  const dataRows = rows.map((r) => {
    const valueDate = r.entryDate
      ? String(r.entryDate).slice(0, 10)
      : String(period.periodTo).slice(0, 10);
    const ref = `MGA-${period.id}-${(r.mgaCrewRequestId ?? '').toString() || '0'}`;
    return [
      r.allotteeName || r.crewName || '',
      r.bank || '',
      r.bankAccount || '',
      money(r.amount).toFixed(2),
      (r.currency || ccy).toUpperCase(),
      valueDate,
      ref,
      r.crewName || '',
      r.rankName || '',
      r.remarks || '',
    ];
  });

  const escape = (v: string | number) => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('\n') || s.includes('"')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [headers.join(','), ...dataRows.map((row) => row.map(escape).join(','))];
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileSlug(period, 'Allotments_Bank')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Fallback remittance rows from ALLOTMENT requests when API remittance is empty. */
export function remittanceRowsFromRequests(
  requests: MgaCrewRequest[],
  lines: MgaCrewLine[],
): MgaRemittanceRow[] {
  const byId = new Map(lines.map((l) => [l.id, l]));
  return requests
    .filter((r) => r.requestType === 'ALLOTMENT')
    .map((r) => {
      const line = byId.get(r.mgaCrewLineId);
      return {
        crewName: r.crewLine?.crewName || line?.crewName || '—',
        rankName: r.crewLine?.rankName || line?.rankName,
        amount: n(r.amount),
        entryDate: r.entryDate,
        remarks: r.remarks,
        bankAccount: undefined,
        bank: undefined,
        allotteeName: undefined,
      };
    });
}

/** Cash book with running balance (period currency). */
export async function exportMgaCashBookStatement(
  payload: MgaExportPayload,
  format: 'excel' | 'pdf',
) {
  const { period, cash } = payload;
  const ccy = period.currency || 'USD';
  const sorted = [...cash].sort((a, b) => {
    const da = a.entryDate ? String(a.entryDate).slice(0, 10) : '';
    const db = b.entryDate ? String(b.entryDate).slice(0, 10) : '';
    if (da !== db) return da.localeCompare(db);
    return a.id - b.id;
  });
  let running = 0;
  const rows: (string | number)[][] = [];
  for (const e of sorted) {
    const amt = n(e.amountPeriod ?? e.amount);
    const signed = e.entryType === 'EXPENSE' ? -amt : amt;
    running = round2(running + signed);
    rows.push([
      e.entryDate ? formatDateGlobal(e.entryDate) : '',
      e.entryType,
      e.description,
      e.crewLine?.crewName || '',
      e.currency || ccy,
      money(e.amount),
      money(e.amountPeriod ?? e.amount),
      money(signed),
      money(running),
      e.remarks || '',
    ]);
  }
  const table: ReportExportTable = {
    headers: [
      'Date',
      'Type',
      'Description',
      'Crew',
      'CCY',
      'Amount',
      'Period amt',
      'Signed',
      'Balance',
      'Remarks',
    ],
    rows,
  };
  const opts: ReportExportOptions = {
    reportTitle: `Cash book — ${periodTitle(period)}`,
    fileName: fileSlug(period, 'CashBook'),
    table,
    rightAlignColumnIndices: [5, 6, 7, 8],
  };
  if (format === 'excel') await exportReportToExcel(opts);
  else await exportReportToPDF(opts);
}



/** Bonded store issues grouped by crew. */
export async function exportMgaBondedByCrew(
  payload: MgaExportPayload,
  format: 'excel' | 'pdf',
) {
  const { period, bonded } = payload;
  const expenses = bonded.filter((l) => l.section === 'EXPENSE');
  const byCrew = new Map<string, { crew: string; lines: MgaBondedLine[]; total: number }>();
  for (const l of expenses) {
    const key = l.crewLine?.crewName || 'Unassigned';
    const cur = byCrew.get(key) || { crew: key, lines: [], total: 0 };
    cur.lines.push(l);
    cur.total = round2(cur.total + n(l.amount));
    byCrew.set(key, cur);
  }
  const groups = Array.from(byCrew.values()).sort((a, b) => a.crew.localeCompare(b.crew));

  const detailRows: (string | number)[][] = [];
  for (const g of groups) {
    for (const l of g.lines) {
      detailRows.push([
        g.crew,
        l.entryDate ? formatDateGlobal(l.entryDate) : '',
        l.description,
        money(l.unitPrice),
        money(l.qty),
        money(l.amount),
        l.remarks || '',
      ]);
    }
    detailRows.push([g.crew, '', 'Subtotal', '', '', money(g.total), '']);
  }

  const summary: ReportExportTable = {
    headers: ['Crew', 'Lines', 'Amount'],
    rows: groups.map((g) => [g.crew, g.lines.length, money(g.total)]),
  };
  const detail: ReportExportTable = {
    headers: ['Crew', 'Date', 'Description', 'Unit', 'Qty', 'Amount', 'Remarks'],
    rows: detailRows,
  };

  const opts: ReportExportOptions = {
    reportTitle: `Bonded by crew — ${periodTitle(period)}`,
    fileName: fileSlug(period, 'BondedByCrew'),
    table: summary,
    rightAlignColumnIndices: [1, 2],
    additionalExcelSheets: [
      {
        name: 'Detail',
        sheetTitle: `Bonded issues detail — ${periodTitle(period)}`,
        table: detail,
        rightAlignColumnIndices: [3, 4, 5],
      },
    ],
    additionalPdfSections: [
      {
        sectionTitle: 'Issue detail',
        table: detail,
        rightAlignColumnIndices: [3, 4, 5],
      },
    ],
  };
  if (format === 'excel') await exportReportToExcel(opts);
  else await exportReportToPDF(opts);
}
